import {
  BoxGeometry,
  CapsuleGeometry,
  Group,
  Mesh,
  MeshLambertMaterial,
  Object3D,
  Quaternion,
  SphereGeometry,
  Vector3,
} from 'three'
import type { Pilot } from '../physics/Pilot'
import type { PilotInput } from '../input/types'

// Deliberately lighter than the city: in VR you look down at your own body
// against dark rooftops, and a dark suit simply is not there.
const SUIT = 0x6e7f9e
const SUIT_DARK = 0x44506b
const TRIM = 0x6fd0ff
const VISOR = 0x0d1622

/** Segment lengths as fractions of standing height. */
const UPPER_ARM = 0.172
const FOREARM = 0.157
const SHOULDER_HALF_WIDTH = 0.105
const SHOULDER_HEIGHT = 0.82
const HIP_HEIGHT = 0.53
const THIGH = 0.245
const SHIN = 0.245

/**
 * The pilot's body (DESIGN.md §5).
 *
 * v1 upper body: torso hung under the head, two-bone IK arms reaching to
 * wherever the controllers actually are. v2 legs: two-segment verlet chains
 * from the pelvis, gravity-driven and purely cosmetic — they sell speed and
 * swing, and cost nothing because they feed no torque back into the body.
 *
 * Everything lives in **tracking space**, as a child of the rig, so the whole
 * avatar rotates with the simulated body while the head and hands stay exactly
 * where the player's real limbs are (§2, one-way coupling).
 */
export class PilotAvatar {
  readonly group = new Group()

  /** Hidden in VR — you would be looking at the inside of your own skull. */
  private readonly head: Object3D
  private readonly torso: Object3D
  private readonly arms: [ArmChain, ArmChain]
  private readonly legs: [LegChain, LegChain]

  constructor() {
    this.group.name = 'avatar'

    this.head = buildHead()
    this.torso = buildTorso()
    this.group.add(this.head, this.torso)

    this.arms = [new ArmChain(), new ArmChain()]
    this.legs = [new LegChain(), new LegChain()]
    for (const arm of this.arms) this.group.add(arm.group)
    for (const leg of this.legs) this.group.add(leg.group)
  }

  /** In VR the head is hidden but everything else stays: you can look down. */
  setFirstPerson(firstPerson: boolean): void {
    this.head.visible = !firstPerson
  }

  setVisible(visible: boolean): void {
    this.group.visible = visible
  }

  update(pilot: Pilot, input: PilotInput, dt: number): void {
    const h = pilot.com.standingHeight
    const com = pilot.com.value

    // The head is player-authoritative; the torso hangs under it, leaning
    // toward the CoM so a physical lean shows up in the body, not just the sim.
    const headPos = input.head.tracked
      ? input.head.position
      : _v1.set(com.x, h, com.z)

    this.head.position.copy(headPos)
    this.head.quaternion.copy(input.head.quaternion)

    const neck = _neck.set(headPos.x, headPos.y - h * 0.09, headPos.z)
    const hip = _hip.set(com.x, h * HIP_HEIGHT, com.z)
    placeBetween(this.torso, neck, hip)

    // Shoulders sit on the neck→hip line, squared to the head's yaw only:
    // cocking your head sideways should not swing your shoulders (§2).
    const yaw = yawOnly(input.head.quaternion, _yawQuat)
    const shoulderCentre = _v2.set(com.x, h * SHOULDER_HEIGHT, com.z)

    for (let i = 0; i < 2; i++) {
      const side = i === 0 ? -1 : 1
      _shoulder
        .set(side * SHOULDER_HALF_WIDTH * h, 0, 0)
        .applyQuaternion(yaw)
        .add(shoulderCentre)
      this.arms[i].solve(_shoulder, input.hands[i].position, h, side)
    }

    // Legs hang from the pelvis under *world* gravity, which in tracking space
    // means whichever way is really down once the body has rotated. Fly
    // inverted and they fall past your head, exactly as they should.
    _gravityLocal.set(0, -1, 0).applyQuaternion(_invBody.copy(pilot.body.orientation).invert())
    for (let i = 0; i < 2; i++) {
      const side = i === 0 ? -1 : 1
      _v3.set(hip.x + side * 0.085 * h, hip.y, hip.z)
      this.legs[i].update(_v3, _gravityLocal, h, dt)
    }
  }
}

// ---------------------------------------------------------------------------
// arms — analytic two-bone IK
// ---------------------------------------------------------------------------

class ArmChain {
  readonly group = new Group()
  private readonly upper: Mesh
  private readonly lower: Mesh
  private readonly elbow: Mesh

  constructor() {
    this.upper = limb(0.052)
    this.lower = limb(0.045)
    this.elbow = new Mesh(
      new SphereGeometry(0.055, 10, 8),
      new MeshLambertMaterial({ color: SUIT_DARK }),
    )
    this.group.add(this.upper, this.lower, this.elbow)
  }

  /**
   * Place the elbow by intersecting two spheres (shoulder+upper, hand+forearm)
   * and picking the point on that circle that hangs outward and down — the
   * closed form, so no iteration and no jitter.
   */
  solve(shoulder: Vector3, hand: Vector3, standingHeight: number, side: number): void {
    const upperLen = UPPER_ARM * standingHeight
    const lowerLen = FOREARM * standingHeight

    _toHand.subVectors(hand, shoulder)
    const reach = Math.min(_toHand.length(), (upperLen + lowerLen) * 0.999)
    if (reach < 1e-4) return
    _dir.copy(_toHand).normalize()

    // Distance along shoulder→hand to the elbow's projection.
    const along = (reach * reach + upperLen * upperLen - lowerLen * lowerLen) / (2 * reach)
    const offset = Math.sqrt(Math.max(0, upperLen * upperLen - along * along))

    // Pole: elbows point down and away from the body.
    _pole.set(side * 0.6, -1, -0.25).normalize()
    _bend.copy(_pole).addScaledVector(_dir, -_pole.dot(_dir))
    if (_bend.lengthSq() < 1e-6) _bend.set(0, -1, 0).cross(_dir)
    _bend.normalize()

    _elbow.copy(shoulder).addScaledVector(_dir, along).addScaledVector(_bend, offset)

    placeBetween(this.upper, shoulder, _elbow)
    placeBetween(this.lower, _elbow, hand)
    this.elbow.position.copy(_elbow)
  }
}

// ---------------------------------------------------------------------------
// legs — verlet, cosmetic only
// ---------------------------------------------------------------------------

class LegChain {
  readonly group = new Group()
  private readonly thigh: Mesh
  private readonly shin: Mesh
  private readonly knee: Mesh
  private readonly foot: Mesh

  private readonly knePos = new Vector3()
  private readonly knePrev = new Vector3()
  private readonly ftPos = new Vector3()
  private readonly ftPrev = new Vector3()
  private seeded = false

  constructor() {
    this.thigh = limb(0.068)
    this.shin = limb(0.055)
    this.knee = new Mesh(new SphereGeometry(0.062, 10, 8), new MeshLambertMaterial({ color: SUIT_DARK }))
    this.foot = new Mesh(
      new BoxGeometry(0.1, 0.06, 0.22),
      new MeshLambertMaterial({ color: SUIT_DARK }),
    )
    this.group.add(this.thigh, this.shin, this.knee, this.foot)
  }

  update(hip: Vector3, gravityLocal: Vector3, standingHeight: number, dt: number): void {
    const thighLen = THIGH * standingHeight
    const shinLen = SHIN * standingHeight

    if (!this.seeded) {
      this.knePos.copy(hip).addScaledVector(gravityLocal, thighLen)
      this.ftPos.copy(this.knePos).addScaledVector(gravityLocal, shinLen)
      this.knePrev.copy(this.knePos)
      this.ftPrev.copy(this.ftPos)
      this.seeded = true
    }

    // Verlet with damping. `dt` is clamped because a stalled frame would
    // otherwise fling the legs off into the next borough.
    const step = Math.min(dt, 1 / 45)
    const damping = 0.92
    const accel = 22

    integrateVerlet(this.knePos, this.knePrev, gravityLocal, accel, damping, step)
    integrateVerlet(this.ftPos, this.ftPrev, gravityLocal, accel, damping, step)

    // Two constraint passes is plenty for a two-link chain.
    for (let i = 0; i < 2; i++) {
      constrain(hip, this.knePos, thighLen)
      constrain(this.knePos, this.ftPos, shinLen)
    }

    placeBetween(this.thigh, hip, this.knePos)
    placeBetween(this.shin, this.knePos, this.ftPos)
    this.knee.position.copy(this.knePos)

    this.foot.position.copy(this.ftPos)
    _legDir.subVectors(this.ftPos, this.knePos).normalize()
    this.foot.quaternion.setFromUnitVectors(_up, _legDir)
  }
}

function integrateVerlet(
  position: Vector3,
  previous: Vector3,
  gravity: Vector3,
  accel: number,
  damping: number,
  dt: number,
): void {
  _velocity.subVectors(position, previous).multiplyScalar(damping)
  previous.copy(position)
  position.add(_velocity).addScaledVector(gravity, accel * dt * dt)
}

/** Pull `b` back onto the sphere of radius `length` around the fixed `a`. */
function constrain(a: Vector3, b: Vector3, length: number): void {
  _delta.subVectors(b, a)
  const d = _delta.length()
  if (d < 1e-6) {
    b.set(a.x, a.y - length, a.z)
    return
  }
  b.copy(a).addScaledVector(_delta, length / d)
}

// ---------------------------------------------------------------------------
// parts
// ---------------------------------------------------------------------------

/** A capsule modelled along +Y with unit length, so it can be stretched. */
function limb(radius: number): Mesh {
  const geometry = new CapsuleGeometry(radius, 1, 3, 8)
  geometry.translate(0, 0.5, 0)
  return new Mesh(geometry, new MeshLambertMaterial({ color: SUIT }))
}

/** Stretch and aim a unit-length +Y limb so it spans `from`→`to`. */
function placeBetween(object: Object3D, from: Vector3, to: Vector3): void {
  _span.subVectors(to, from)
  const length = _span.length()
  object.position.copy(from)
  if (length < 1e-5) return
  _span.multiplyScalar(1 / length)
  object.quaternion.setFromUnitVectors(_up, _span)
  object.scale.set(1, length, 1)
}

function buildHead(): Object3D {
  const group = new Group()
  const helmet = new Mesh(
    new SphereGeometry(0.125, 16, 12),
    new MeshLambertMaterial({ color: SUIT }),
  )
  group.add(helmet)

  const visor = new Mesh(
    new SphereGeometry(0.127, 16, 12, Math.PI * 0.25, Math.PI * 0.5, Math.PI * 0.28, Math.PI * 0.44),
    new MeshLambertMaterial({ color: VISOR, emissive: 0x0a2030 }),
  )
  // The sphere's default sector faces +X; swing it round to the head's −Z.
  visor.rotation.y = -Math.PI / 2
  group.add(visor)

  const light = new Mesh(
    new BoxGeometry(0.05, 0.02, 0.03),
    new MeshLambertMaterial({ color: TRIM, emissive: TRIM, emissiveIntensity: 0.8 }),
  )
  light.position.set(0.1, 0.06, -0.04)
  group.add(light)

  return group
}

function buildTorso(): Object3D {
  const group = new Group()

  const chest = new Mesh(
    new CapsuleGeometry(0.155, 1, 4, 12),
    new MeshLambertMaterial({ color: SUIT }),
  )
  chest.geometry.translate(0, 0.5, 0)
  chest.scale.set(1, 1, 0.72)
  group.add(chest)

  // Fuel pack on the back, so "which way am I facing" is readable at a glance
  // from the chase camera and from another player later.
  const pack = new Mesh(
    new BoxGeometry(0.26, 0.34, 0.13),
    new MeshLambertMaterial({ color: SUIT_DARK }),
  )
  pack.position.set(0, 0.42, 0.17)
  group.add(pack)

  const stripe = new Mesh(
    new BoxGeometry(0.055, 0.3, 0.02),
    new MeshLambertMaterial({ color: TRIM, emissive: TRIM, emissiveIntensity: 0.55 }),
  )
  stripe.position.set(0, 0.42, 0.24)
  group.add(stripe)

  return group
}

const _v1 = new Vector3()
const _v2 = new Vector3()
const _v3 = new Vector3()
const _neck = new Vector3()
const _hip = new Vector3()
const _shoulder = new Vector3()
const _span = new Vector3()
const _toHand = new Vector3()
const _dir = new Vector3()
const _pole = new Vector3()
const _bend = new Vector3()
const _elbow = new Vector3()
const _velocity = new Vector3()
const _delta = new Vector3()
const _legDir = new Vector3()
const _gravityLocal = new Vector3()
const _up = new Vector3(0, 1, 0)
const _yawQuat = new Quaternion()
const _invBody = new Quaternion()

/** Strip pitch and roll, keeping only the heading. */
function yawOnly(q: Quaternion, out: Quaternion): Quaternion {
  const yaw = Math.atan2(2 * (q.w * q.y + q.x * q.z), 1 - 2 * (q.y * q.y + q.z * q.z))
  return out.setFromAxisAngle(_up, yaw)
}
